import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';

const ForgotPassword = ({ show, onHide, defaultEmail }) => {
  const [step, setStep] = useState(1); // 1 = enter email, 2 = enter code + new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show) {
      setEmail(defaultEmail || '');
      setCode('');
      setNewPassword('');
      setError('');
      setSuccess('');
      setStep(1);
    }
  }, [show, defaultEmail]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await resetPassword({ username: email });
      setSuccess('Password reset code sent to your email');
      setCode('');
      setStep(2);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      setSuccess('Password reset successful! You can now login with your new password.');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Confirm reset error:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail('');
    setCode('');
    setNewPassword('');
    setError('');
    setSuccess('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Forgot Password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {step === 1 && (
          <Form onSubmit={handleRequestReset}>
            <p>Enter your email address and we'll send you a code to reset your password.</p>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="username"
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Code'
                )}
              </Button>
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
            </div>
          </Form>
        )}

        {step === 2 && (
          <Form onSubmit={handleConfirmReset}>
            <p>Enter the code sent to your email and your new password.</p>

            <Form.Group className="mb-3">
              <Form.Label>Reset Code</Form.Label>
              <Form.Control
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                name="confirmationCode"
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={12}
              />
              <Form.Text className="text-muted">
                Minimum 12 characters with uppercase, lowercase, number, and symbol
              </Form.Text>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ForgotPassword;
